import { HotHeatMapResult } from "..";

/**
 * Abstract base class for heat map metadata and logic in Handsontable grids.
 * Provides storage, color configuration, and utility methods for heat map calculations.
 */
export abstract class HotHeatMapBaseMeta {

  // ---------------- Properties  --------------------

  /**
   * Internal string store for key-value pairs used in heat map calculations.
   */
  public store: string | null = null;

  /**
   * The column index associated with this heat map meta.
   */
  public column: number | null = null;

  /**
   * The primary color used for the heat map.
   */
  public colorHm: string | null = '#777';

  /**
   * The background color used for the heat map.
   */
  public colorHmBg: string | null = '#ffffff';

  // ---------------- Constants  --------------------

  /**
   * Regular expression pattern for matching keys in the store.
   */
  protected readonly expr: string = "[a-zA-Z0-9#.-]*";

  /**
   * The default color for the heat map.
   */
  public readonly colorDefault: string = '#777';

  /**
   * The default background color for the heat map.
   */
  public readonly colorDefaultBg: string = '#ffffff';

  // ---------------- Methods  --------------------

  /**
   * Initializes the heat map meta for a specific column and optional metadata.
   * @param column The column index.
   * @param meta Optional metadata string.
   */
  public abstract init(column: number, meta?: string | null): void;

  /**
   * Initializes the range values for the heat map.
   * @param values The array of values to use for range calculations.
   */
  public abstract initRangeValues(values: any[]): void;

  /**
   * Calculates the heat map colors for a given value.
   * @param value The value to calculate colors for.
   * @returns The result containing color information.
   */
  public abstract calcHeatmapColors(value: number | null): HotHeatMapResult;

  /**
   * Clears the internal store.
   */
  public abstract clearStore(): void;

  // ---------------- Functions  --------------------

  /**
   * Retrieves a value from the internal store by key.
   * @param key The key to look up.
   * @param isNumber Whether to return the value as a number.
   * @returns The value associated with the key, or null if not found.
   */
  protected getStoreValue(key: string, isNumber: boolean): any | null {

    if (this.store == null || this.store == undefined) {
      return null;
    }

    const keyMatch: string = "\\" + key + this.expr;
    const regex = new RegExp(keyMatch, "g");

    const matches = this.store.match(regex);

    if (matches != null && matches.length > 0) {

      const match = matches[0];

      if (isNumber === true)
        return Number(match.replace(key, ""));
      else
        return match.replace(key, "");
    }

    return null;
  }

  /**
   * Sets a value in the internal store for a given key.
   * If the key exists, it updates the value; otherwise, it adds a new key-value pair.
   * @param value The value to set.
   * @param key The key to associate with the value.
   */
  protected setStoreValue(value: any | null, key: string): void {

    if (this.store == null || this.store == undefined) {
      this.store = "";
    }

    const keyMatch: string = "\\" + key + this.expr;
    const regex = new RegExp(keyMatch, "g");

    const matches = this.store.match(regex);

    if (matches != null && matches.length > 0 && value !== null && value !== undefined) {
      this.store = this.store.replace(matches[0], key + (value ?? ""));
    }
    else {
      // Key not found so add it to the store
      this.store += " " + key + (value ?? "");
    }

  }

  /**
   * Calculates the absolute range between two numbers.
   * @param a The first number.
   * @param b The second number.
   * @returns The absolute difference between a and b.
   */
  protected calculateRange(a: number, b: number): number {

    let range = Math.max(a, b) - Math.min(a, b);
    if (range < 0)
      range *= -1;

    return range;
  }

  /**
   * Calculates the percentage position of a value within a range defined by a and b.
   * @param value The value to evaluate.
   * @param a The start of the range.
   * @param b The end of the range.
   * @returns The percentage position (0 to 1), or null if the range is zero or value is null.
   */
  protected calculatePercentagePosition(value: number | null, a: number, b: number): number | null {

    let range = Math.max(a, b) - Math.min(a, b);
    if (range < 0)
      range *= -1;

    if (range === 0 || value === null) {
      return null;
    }

    let position = value - Math.min(a, b);

    if (position < 0)
      position *= -1;

    const percentage = (position / range);

    return percentage;
  }
}


